<template>
  <div id="app">
    <div id="nav">
      <b-navbar toggleable="lg" type="dark" variant="dark" fixed="top">
        <b-navbar-brand :to="{ name: 'home' }">
          <img src="./assets/logo.png" height="42" width="42" />Manage Ownership
        </b-navbar-brand>
        <b-navbar-toggle target="nav-collapse"></b-navbar-toggle>
        <b-collapse id="nav-collapse" is-nav>
            <b-navbar-nav>
                <li v-if="!isDCOUser" class="nav-item">
                    <router-link :to="{ name: 'submission/create' }" class="nav-link">New NJCRIB ERM-14 Form</router-link>
                </li>
                <li>
                    <router-link :to="{ name: 'submission/list'}" class="nav-link">{{ isDCOUser ? 'Search NJCRIB ERM-14 Forms' : 'My NJCRIB ERM-14 Forms' }}</router-link>
                </li>
                <li>
                    <router-link :to="{ name: 'faq/faq'}" class="nav-link"><b style="font-weight: bolder">FAQ</b></router-link>
                </li>
            </b-navbar-nav>
          <b-navbar-nav class="ml-auto">
            <router-link :to="{ name: this.loggedIn ? 'account/update' : 'account/login' }" class="nav-link">
              <span class="fa-stack fa-1x">
                <i class="fas fa-circle text-light fa-stack-2x" style="font-size: 1.75rem; top: 3px;"></i>
                <i class="fas fa-user-circle text-success align-middle fa-stack-2x"></i>
              </span>
              <span class="ml-1">{{ username || 'Login' }}</span>
            </router-link>
            <router-link v-if="!loggedIn" :to="{ name: 'account/register' }" class="nav-link">Register</router-link>
            <a href="#" v-else @click.prevent="logout" class="nav-link">Logout</a>
          </b-navbar-nav>
        </b-collapse>
      </b-navbar>
    </div>
    <div id="body-content">
      <back-button v-if="showBackButton" buttonClass="btn-success" @back-clicked="backClicked">{{ backButtonText }}</back-button>
      <div class="col-xl-5 mx-auto">
        <b-breadcrumb v-if="showBreadcrumbs" :items="breadcrumbs"></b-breadcrumb>
      </div>
      <transition :name="transitionName" mode="out-in">
        <router-view />
      </transition>
      <next-button v-if="showNextButton" buttonClass="btn-success" @next-clicked="nextClicked">{{ nextButtonText }}</next-button>
    </div>

    <b-modal 
      v-model="showConfirmLeaveModal" 
      ref="unsaved-changes-modal" 
      title="You have unsaved changes" 
      header-text-variant="dark" 
      body-text-variant="dark" 
      centered
      hide-header-close
      no-close-on-backdrop
      no-close-on-esc>
      <template slot="default">
        You have unsaved changes. Would you like to save your changes, discard them, or cancel to stay on this page?
      </template>
      <template slot="modal-footer" class="float-right">
        <b-button variant="success" @click.prevent="$EventBus.$emit('confirm-leave', ConfirmLeaveEventTypes.Save)">Save Changes</b-button>
        <b-button variant="danger" @click.prevent="$EventBus.$emit('confirm-leave', ConfirmLeaveEventTypes.Discard)">Discard Changes</b-button>
        <b-button variant="secondary" @click.prevent="$EventBus.$emit('confirm-leave', ConfirmLeaveEventTypes.Cancel)">Cancel</b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import NProgress from 'nprogress'
import UserService from './services/UserService.js'

export default {
  name: 'App',
  data() {
    return {}
  },
  methods: {
    logout() {
      this.$store.dispatch('user/logout')
      UserService.logout().finally(() => {
        this.$store.dispatch('user/setToken', '')
      })
      this.$router.push({
        name: 'home'
      })
    },
    backClicked() {
      this.$store.dispatch('application/setDirection', false).then(() => {
        this.$EventBus.$emit('back-clicked')
      })
    },
    nextClicked() {
      this.$store.dispatch('application/setDirection', true).then(() => {
        this.$EventBus.$emit('next-clicked')
      })
    }
  },
  computed: {
    loggedIn() {
      return this.$store.state.user.user.email
    },
    username() {
      return this.loggedIn ? this.$store.state.user.user.name : ''
    },
    transitionName() {
      return this.$store.state.application.transition
    },
    showBackButton() {
      return this.$store.state.application.showBackButton
    },
    showNextButton() {
      return this.$store.state.application.showNextButton
    },
    backButtonText() {
      return this.$store.state.application.backButtonText
    },
    nextButtonText() {
      return this.$store.state.application.nextButtonText
    },
    breadcrumbs() {
      return this.$store.state.application.breadcrumbs
    },
    showBreadcrumbs() {
      return this.$store.state.application.breadcrumbs && this.$store.state.application.breadcrumbs.length > 0
    },
    isDCOUser() {
      return this.$store.getters['user/isDCOUser']
    },
    showConfirmLeaveModal() {
      return this.$store.state.application.showConfirmLeaveModal
    }
  },
  created() {
    NProgress.configure({
      parent: '#body-content'
    })

    this.$EventBus.$on('confirm-modal-show', () => this.$refs['unsaved-changes-modal'].show())
  }
}
</script>

<style>
.move-back-enter-active {
  transition: opacity .1s ease-out .1s, transform .25s ease-out;
}

.move-back-leave-active {
  transition: opacity .1s ease-in, transform .25s ease-in;
}

.move-back-enter-to {
  transform: translateX(0%);
  opacity: 1;
}

.move-back-enter {
  transform: translateX(-100%);
  opacity: 0;
}

.move-back-leave-to {
  transform: translateX(100%);
  opacity: 0;
}

.move-next-enter-active {
  transition: opacity .1s ease-out .1s, transform .25s ease-out;
}

.move-next-leave-active {
  transition: opacity .1s ease-in, transform .25s ease-in;
}

.move-next-enter-to {
  transform: translateX(0%);
  opacity: 1;
}

.move-next-enter {
  transform: translateX(100%);
  opacity: 0;
}

.move-next-leave-to {
  transform: translateX(-100%);
  opacity: 0;
}

.fade-in-enter-active {
  transition: opacity .5s;
}

.fade-in-leave-active {
  transition: opacity .1s ease-in, transform .25s ease-in;
}

.fade-in-enter-to {
  opacity: 1;
}

.fade-in-enter {
  opacity: 0;
  transform: translateX(-100%);
}

.fade-in-leave-to {
  opacity: 0;
  transform: translateX(100%);
}

.hover:hover {
    text-decoration: none;
    animation-duration: .6s;
    animation-name: float;
    animation-iteration-count: infinite;
    animation-direction: alternate;
    animation-timing-function: ease-in-out;
}

@keyframes float {
    from {
        top: 0px;
    }

    to {
        top: -4px;
    }
}

#app {
  font-family: 'Roboto', 'Avenir', Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  overflow-x: hidden;
}

#nav a.router-link-exact-active {
  color: #28a745;
}

    #nav a:not(.router-link-exact-active) {
        color: whitesmoke;
    }

#nprogress .bar {
  background: #28a745 !important;
}

#nprogress .spinner-icon {
  border-top-color: #28a745 !important;
  border-left-color: #28a745 !important;
}

.divider {
  margin-left: 6px;
  margin-right: 6px;
}

.custom-control-input:checked ~ .custom-control-label::before {
  border-color: #28a745 !important;
  background-color: #28a745 !important;
}

#nav nav {
  box-shadow: 0 0 10px 0 black;
}

html,
body {
  background-color: #1b1c1d !important;
  color: white !important;
  line-height: 2em !important;
  overflow-x: hidden;
}

.col-form-label {
  padding-bottom: 0 !important;
}

#body-content {
  padding-top: 2em;
  margin-top: 68px;
}

body {
  font-size: 1.05em;
}

@media (min-width: 1200px) {
  .col-xl-5 {
    -ms-flex: 0 0 41.666666%;
    -webkit-box-flex: 0;
    flex: 0 0 41.666666%;
    max-width: 41.666666%;
  }
}

.text-muted {
  color: #ababab !important;
}

.breadcrumb-item a, .account-item a.account-link {
  color: rgba(255, 255, 255, 0.5);
}

.breadcrumb-item a:hover {
  color: rgba(255, 255, 255, 0.75);
}

.account-item a.account-link:hover {
  color: #28a745;
}

.breadcrumb-item.active {
  color: #28a745 !important;
}

.breadcrumb {
  background-color: #1b1c1d !important;
  padding-top: 0 !important;
  padding-bottom: 0 !important;
  margin-bottom: .5rem !important;
  border-left: 3px solid #28a745;
  border-radius: 3px !important;
}

table.table thead tr th {
  border-top: 0 !important;
}

table.table {
  border-radius: 0.5rem !important;
  line-height: 1.5em !important;
}

table.table td {
  vertical-align: middle !important;
  word-break: break-word;
}

.form-group {
  margin-bottom: 2rem !important;
}

.form-row .form-group {
  margin-bottom: 0 !important;
}

.modal-body, .modal, .modal-dialog {
  line-height: 1.75em !important;
}

.action-buttons {
  white-space: nowrap !important;
  padding: 0.5rem !important;
  width: 15% !important;
  text-align: left !important;
}

.vdp-datepicker__calendar .cell.selected {
  background-color: #28a745 !important;
}

.vdp-datepicker__calendar {
  color: black !important;
}

.calendar-top {
  top: -283px !important;
}

.tooltip-inner {
  background-color: #6c757d !important;
  color: white !important;
}

.tooltip .arrow::before {
  border-top-color: #6c757d !important;
}

.pagination .page-link {
  color: #28a745 !important;
}

.pagination .page-item.active .page-link {
  color: white !important;
  background-color: #28a745 !important;
}

.pagination .page-item.disabled .page-link {
  color: #6c757d !important;
}

.pagination .page-link {
  color: #28a745 !important;
}

.pagination .page-item.active .page-link {
  color: white !important;
  background-color: #28a745 !important;
}

.pagination .page-item.disabled .page-link {
  color: #6c757d !important;
}
</style>
